import { adaptRunDetail, runDetailItemsAsView } from '../service/adaptRun'
import { isTerminalRunStatus } from '../models/insightAction.types'
import type { InsightActionStore } from '../stores/insightActionStore'

import type { InsightActionApiSlice, RunScope } from './insightActionFlow.types'

export const POLL_CADENCE_MS = [1000, 2000, 5000] as const

const polling = new Set<string>()

export function resetPollRegistry(): void {
  polling.clear()
}

export interface PollRunFlowDeps {
  api: InsightActionApiSlice<'runDetail'>
  store: InsightActionStore
  wait?: (ms: number) => Promise<void>
}

function defaultWait(ms: number): Promise<void> {
  return new Promise((resolve) => {
    setTimeout(resolve, ms)
  })
}

export class PollRunFlow {
  private readonly wait: (ms: number) => Promise<void>

  constructor(private readonly deps: PollRunFlowDeps) {
    this.wait = deps.wait ?? defaultWait
  }

  async execute(scope: RunScope, key: string): Promise<void> {
    const store = this.deps.store
    const runId = store.runs[key]?.runId
    if (!runId || polling.has(runId)) return
    polling.add(runId)

    try {
      for (let tick = 0; ; tick += 1) {
        await this.wait(POLL_CADENCE_MS[Math.min(tick, POLL_CADENCE_MS.length - 1)] ?? 5000)
        if (!store.panelOpen || store.runs[key]?.runId !== runId) return

        const settled = await this.readOnce(scope, key, runId)
        if (settled) return
      }
    } finally {
      polling.delete(runId)
    }
  }

  private async readOnce(scope: RunScope, key: string, runId: string): Promise<boolean> {
    const store = this.deps.store
    try {
      const detail = adaptRunDetail(
        await this.deps.api.runDetail(scope.accountId, scope.workspaceId, runId),
      )
      if (store.runs[key]?.runId !== runId) return true

      store.setRun(key, {
        runId,
        insightId: detail.insightId,
        status: detail.status,
        totalItems: detail.totalItems,
        counts: detail.counts,
        detail,
        items: runDetailItemsAsView(detail),
      })

      if (!isTerminalRunStatus(detail.status)) return false
      if (store.step === 'progress') store.setStep('result')
      return true
    } catch {
      return false
    }
  }
}
